import {
  Collection,
  CollectionColumn,
  DataType,
  DecimalScale,
  Form,
  FormOptions,
  Section,
  SelectOption
} from '@globaleris/ui.library'
import { makeStyles, Typography, useTheme } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumTipoOperacion } from '../../../api'
import Page from '../../../components/page/page'
import { enumKeyToValue, enumValueToKey } from '../../../helpers'
import { getCustomerDomain } from '../../../redux'
import {
  fetchDestinations,
  fetchLogisticElementsToItems,
  fetchOrigins,
  fetchOwners,
  saveTravel
} from '../ships.service'
import { styles } from './ships.add.styles'

export interface ShipsAddPageProps {}

export const ShipsAddPage: React.FC<ShipsAddPageProps> = () => {
  const [t] = useTranslation(['globalerisLibrary', 'shipsAdd', 'enums'])

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const [ships, setShips] = useState<SelectOption[]>([])
  const [shipCapacity, setShipCapacity] = useState<number>(100000)
  const [activeFilter, setActiveFilter] = useState<string | number>()
  const [origins, setOrigins] = useState<SelectOption[]>([])
  const [destinations, setDestinations] = useState<SelectOption[]>([])
  const [owners, setOwners] = useState<SelectOption[]>([])
  const [logisticElements, setLogisticElements] = useState<SelectOption[]>([])

  useEffect(() => {
    const getDestinations = async () => {
      setDestinations(await fetchDestinations())
    }

    const getOwners = async () => {
      setOwners(await fetchOwners())
    }

    const getLogisticElements = async () => {
      setLogisticElements(await fetchLogisticElementsToItems())
    }

    getDestinations()
    getOwners()
    getLogisticElements()
  }, [])

  useEffect(() => {
    const getOrigins = async (type: number | string) => {
      setOrigins(await fetchOrigins(type))
    }

    if (activeFilter) {
      getOrigins(activeFilter)
    }
  }, [activeFilter])

  //#region Form and Datatable Definitions

  const formSections: Section[] = [
    {
      inputs: [
        {
          name: 'date',
          label: t('shipsAdd:form.inputs.date'),
          type: DataType.DATE,
          disablePast: true,
          required: true
        },
        {
          name: 'ship',
          label: t('shipsAdd:form.inputs.ship'),
          type: DataType.SELECT,
          selectOptions: ships,
          onChange: () => {
            setShipCapacity(100000)
          },
          required: true
        },
        {
          name: 'capacity',
          label: t('shipsAdd:form.inputs.shipCapacity'),
          type: DataType.NUMBER,
          decimalScale: DecimalScale.VOLUME,
          readOnly: true,
          defaultValue: shipCapacity
          //required: true
        },
        {
          name: 'typeFilter',
          label: t('shipsAdd:form.inputs.filter'),
          type: DataType.SELECT,
          selectOptions: [
            {
              value: EnumTipoOperacion.DescargaBuque,
              label: enumKeyToValue(EnumTipoOperacion, EnumTipoOperacion.DescargaBuque)
            },
            {
              value: EnumTipoOperacion.CargaBuque,
              label: enumKeyToValue(EnumTipoOperacion, EnumTipoOperacion.CargaBuque)
            }
          ],
          onChange: (value: string | number) => {
            setActiveFilter(value)
          },
          required: true
        },
        {
          name: 'origin',
          label: t('shipsAdd:form.inputs.origin'),
          type: DataType.SELECT,
          //readOnly: true,
          selectOptions: origins,
          required: true
        },
        {
          name: 'destination',
          label: t('shipsAdd:form.inputs.destination'),
          type: DataType.MULTISELECT,
          readOnly:
            enumValueToKey(EnumTipoOperacion, activeFilter) ===
            enumValueToKey(EnumTipoOperacion, EnumTipoOperacion.DescargaBuque)
              ? false
              : true,
          selectOptions: destinations,
          required:
            enumValueToKey(EnumTipoOperacion, activeFilter) ===
            enumValueToKey(EnumTipoOperacion, EnumTipoOperacion.DescargaBuque)
              ? true
              : false
        },
        {
          name: 'quantity',
          label: t('shipsAdd:form.inputs.quantity'),
          type: DataType.NUMBER,
          decimalScale: DecimalScale.ENERGY,
          required: true
        },
        {
          name: 'share',
          label: t('shipsAdd:form.inputs.share'),
          type: DataType.SWITCH,
          required: true
        },
        {
          name: 'soe',
          label: t('shipsAdd:form.inputs.soe'),
          type: DataType.SWITCH,
          required: true
        },
        {
          name: 'owner',
          label: t('shipsAdd:form.inputs.owner'),
          type: DataType.SELECT,
          //readOnly: true,
          selectOptions: owners
        },
        {
          name: 'marketers',
          label: t('shipsAdd:form.inputs.marketers'),
          type: DataType.MULTISELECT,
          //readOnly: true,
          selectOptions: owners
        }
      ]
    }
  ]

  const formOptions: FormOptions = {
    elevation: 1,
    columns: 3,
    analytics: {
      customer: getCustomerDomain(),
      view: 'Ships Add Form'
    },
    onSave: async (data: any) => {
      if (data) {
        handlerSaveData(data)
      }

      return true
    }
  }

  const columnsForOperationsCollection: CollectionColumn[] = [
    {
      name: 'date',
      label: t('shipsAdd:datatable.columns.date'),
      type: DataType.DATE,
      disablePast: true,
      required: true
    },
    {
      name: 'commercialPlant',
      label: t('shipsAdd:datatable.columns.commercialPlant'),
      type: DataType.SELECT,
      required: true,
      selectOptions: logisticElements
    },
    {
      name: 'quantity',
      label: t('shipsAdd:datatable.columns.quantity'),
      type: DataType.NUMBER,
      decimalScale: DecimalScale.ENERGY,
      required: true,
      defaultValue: shipCapacity
    }
  ]

  const optionsForOperationsCollection = {
    elevation: 2,
    canEdit: true,
    canDelete: true,
    canInsert: true,
    fabLabel: 'Nueva Operación'
  }

  const operationsCollection: Collection = {
    name: 'operations',
    title: 'Operaciones',
    columns: columnsForOperationsCollection,
    options: optionsForOperationsCollection
  }

  const collections = [operationsCollection]

  //#endregion

  //#region Hadlers

  const handlerSaveData = (data: any) => {
    saveTravel(data).then(response => {})
  }

  //#endregion

  return (
    <Page>
      <div className={classes.appBar}>
        <Typography variant="h1" color="secondary" className={classes.title}>
          {t('translation:pages.ships.title')}
        </Typography>
      </div>
      <Form sections={formSections} data={{}} options={formOptions} collections={collections} />
      {/* <DataTable data={ships} columns={dataTableColumns} options={dataTableOptions} /> */}
    </Page>
  )
}
